/**
 * JWT utility functions for parsing Google ID tokens
 */

interface JwtPayload {
  exp: number;  // Expiry timestamp (seconds since epoch)
  iat: number;  // Issued at timestamp
  email?: string;
  name?: string;
  sub?: string; // Subject (user ID)
}

/**
 * Parse a JWT and return its payload
 * Note: This does NOT verify the signature - server-side verification is required
 */
export function parseJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    // Base64url decode
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Get token expiry time in milliseconds since epoch
 */
export function getTokenExpiry(token: string): number | null {
  const payload = parseJwt(token);
  return payload?.exp ? payload.exp * 1000 : null;
}

/**
 * Check if token is expired or about to expire
 * @param token JWT token
 * @param bufferMs Buffer time in ms before actual expiry to consider it "expired" (default 5 min)
 */
export function isTokenExpired(token: string, bufferMs: number = 5 * 60 * 1000): boolean {
  const expiry = getTokenExpiry(token);
  if (!expiry) return true;
  return Date.now() >= expiry - bufferMs;
}

/**
 * Get time remaining until token expires in milliseconds
 */
export function getTimeUntilExpiry(token: string): number {
  const expiry = getTokenExpiry(token);
  if (!expiry) return 0;
  return Math.max(0, expiry - Date.now());
}
